import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { format, parseISO } from 'date-fns';
import { Trophy, Plus, X, Trash, Ruler, Gear, ArrowsDownUp, ArrowUp, ArrowDown, CaretDown } from '@phosphor-icons/react';
import GoalsPanel from '../components/GoalsPanel';
import ActivityHeatmap from '../components/ActivityHeatmap';
import StreakWidget from '../components/StreakWidget';
import Avatar from '../components/Avatar';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MEASUREMENT_FIELDS = [
  { key: 'weight_kg',    label: 'Weight',    unit: 'kg' },
  { key: 'height_cm',    label: 'Height',    unit: 'cm' },
  { key: 'body_fat_pct', label: 'Body Fat',  unit: '%'  },
  { key: 'chest_cm',     label: 'Chest',     unit: 'cm' },
  { key: 'waist_cm',     label: 'Waist',     unit: 'cm' },
  { key: 'hip_cm',       label: 'Hip',       unit: 'cm' },
  { key: 'arm_cm',       label: 'Arm',       unit: 'cm' },
  { key: 'thigh_cm',     label: 'Thigh',     unit: 'cm' },
];

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [exercises, setExercises] = useState([]);
  const [selectedExerciseId, setSelectedExerciseId] = useState('');
  const [progressData, setProgressData] = useState(null);
  const [total, setTotal] = useState(null);
  const [pbs, setPbs] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resetArmed, setResetArmed] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [showAllComparisons, setShowAllComparisons] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [ex, t, p, m] = await Promise.all([
        axios.get(`${API_URL}/exercises`, { withCredentials: true }),
        axios.get(`${API_URL}/progress/total`, { withCredentials: true }),
        axios.get(`${API_URL}/progress/pbs`, { withCredentials: true }),
        axios.get(`${API_URL}/measurements`, { withCredentials: true }),
      ]);
      setExercises(ex.data);
      setTotal(t.data);
      setPbs(p.data);
      setMeasurements(m.data);
      const withData = p.data[0]?.exercise_id;
      if (withData) setSelectedExerciseId(withData);
      else if (ex.data.length > 0) setSelectedExerciseId(ex.data[0].id);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadExerciseProgress = useCallback(async () => {
    if (!selectedExerciseId) return;
    try {
      const { data } = await axios.get(
        `${API_URL}/progress/exercise/${selectedExerciseId}`,
        { withCredentials: true }
      );
      setProgressData(data);
    } catch (e) { console.error('[breth] load exercise progress failed', e); }
  }, [selectedExerciseId]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (selectedExerciseId) loadExerciseProgress(); }, [selectedExerciseId, loadExerciseProgress]);

  const saveMeasurement = async (payload) => {
    try {
      await axios.post(`${API_URL}/measurements`, payload, { withCredentials: true });
      const { data } = await axios.get(`${API_URL}/measurements`, { withCredentials: true });
      setMeasurements(data);
      setShowLog(false);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save');
    }
  };

  const deleteMeasurement = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API_URL}/measurements/${id}`, { withCredentials: true });
      setMeasurements((prev) => prev.filter((m) => m.id !== id));
    } catch (e) {
      console.error('[breth] delete measurement failed', e);
      alert('Could not delete entry — please try again.');
    }
  };

  const exerciseMap = useMemo(() => {
    const m = {};
    exercises.forEach((e) => { m[e.id] = e; });
    return m;
  }, [exercises]);

  const topComparison = total?.comparisons?.slice().reverse().find((c) => c.count >= 1) || total?.comparisons?.[0];

  // Two-step reset: arm on first tap, confirm on second. Auto-disarms after 4s.
  useEffect(() => {
    if (!resetArmed) return;
    const t = setTimeout(() => setResetArmed(false), 4000);
    return () => clearTimeout(t);
  }, [resetArmed]);

  const handleReset = async () => {
    if (!resetArmed) {
      setResetArmed(true);
      return;
    }
    setResetArmed(false);
    await logout();
  };

  if (loading) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center">
        <div className="animate-pulse-soft text-6xl font-black font-heading text-graphite">B</div>
      </div>
    );
  }

  const chartData =
    progressData?.history?.map((h) => ({
      date: format(new Date(h.date), 'MMM d'),
      value: h.estimated_1rm,
    })) || [];

  // Section registry — order can be tweaked by the user (top summary is pinned)
  const DEFAULT_ORDER = ['streak', 'body', 'activity', 'goals', 'volume', 'exercise', 'pbs'];
  const savedOrder = Array.isArray(user?.profile_layout) && user.profile_layout.length > 0
    ? user.profile_layout.filter((k) => DEFAULT_ORDER.includes(k))
    : DEFAULT_ORDER;
  // Any new section keys added after the user set their layout get appended
  const fullOrder = [...savedOrder, ...DEFAULT_ORDER.filter((k) => !savedOrder.includes(k))];

  const moveSection = (idx, delta) => {
    const next = [...fullOrder];
    const to = idx + delta;
    if (to < 0 || to >= next.length) return;
    [next[idx], next[to]] = [next[to], next[idx]];
    setUser({ ...user, profile_layout: next });
    axios
      .put(`${API_URL}/auth/me`, { profile_layout: next }, { withCredentials: true })
      .catch(() => {});
  };

  const resetLayout = () => {
    setUser({ ...user, profile_layout: DEFAULT_ORDER });
    axios
      .put(`${API_URL}/auth/me`, { profile_layout: DEFAULT_ORDER }, { withCredentials: true })
      .catch(() => {});
  };

  const SECTIONS = {
    streak: { label: 'Streak', render: () => <StreakWidget /> },
    body: {
      label: 'Body',
      render: () => (
        <MeasurementsCard
          measurements={measurements}
          onAdd={() => setShowLog(true)}
          onDelete={deleteMeasurement}
        />
      ),
    },
    activity: { label: 'Yearly Activity', render: () => <ActivityHeatmap /> },
    goals: { label: 'Goals', render: () => <GoalsPanel exercises={exercises} /> },
    volume: {
      label: 'Volume Perspective',
      render: () =>
        total?.comparisons?.length > 1 ? (
          <VolumePerspectiveCard
            comparisons={total.comparisons}
            expanded={showAllComparisons}
            onToggle={() => setShowAllComparisons((v) => !v)}
          />
        ) : null,
    },
    exercise: {
      label: 'By Exercise',
      render: () => (
        <div className="ios-card p-4 mb-4">
          <h2 className="text-lg font-black font-heading uppercase text-graphite mb-3">
            By Exercise
          </h2>
          <select
            value={selectedExerciseId}
            onChange={(e) => setSelectedExerciseId(e.target.value)}
            className="w-full h-12 px-4 rounded-ios-pill border border-hairline bg-boneWarm text-sm font-semibold mb-4"
            data-testid="exercise-select"
          >
            {exercises.map((ex) => (
              <option key={ex.id} value={ex.id}>
                {ex.name} ({ex.muscle_group})
              </option>
            ))}
          </select>

          {progressData && (
            <>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <StatCard label="Estimated 1RM" value={Math.round(progressData.estimated_1rm)} unit="kg" />
                <StatCard label="Best Top Set" value={Math.round(progressData.best_weight)} unit="kg" />
              </div>
              {chartData.length > 0 ? (
                <div className="bg-boneWarm rounded-ios p-3">
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={chartData}>
                      <CartesianGrid stroke="#DCD7CB" strokeDasharray="0" vertical={false} />
                      <XAxis dataKey="date" stroke="#191C21" style={{ fontSize: '9px', fontFamily: 'Inter', fontWeight: 600 }} />
                      <YAxis stroke="#191C21" style={{ fontSize: '11px', fontFamily: 'Barlow Condensed', fontWeight: 900 }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#191C21', color: '#EDEAE3', border: 'none', borderRadius: '12px' }}
                        labelStyle={{ color: '#EDEAE3', fontSize: '10px', textTransform: 'uppercase' }}
                        itemStyle={{ color: '#EDEAE3' }}
                      />
                      <Line type="monotone" dataKey="value" stroke="#C0392F" strokeWidth={3} dot={{ fill: '#C0392F', r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-graphite/50 text-center py-6">
                  No data yet for this exercise
                </p>
              )}
            </>
          )}
        </div>
      ),
    },
    pbs: {
      label: 'Personal Bests',
      render: () =>
        pbs.length > 0 ? (
          <div className="ios-card p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={18} weight="fill" className="text-userRed" />
              <h2 className="text-lg font-black font-heading uppercase text-graphite">
                Personal Bests
              </h2>
            </div>
            <div className="space-y-2">
              {pbs.slice(0, 20).map((pb, i) => {
                const ex = exerciseMap[pb.exercise_id];
                const cardio = pb.set_type === 'cardio';
                return (
                  <div key={`${pb.exercise_id}-${pb.logged_at}`} className="flex items-center justify-between py-2 border-b border-hairline last:border-0" data-testid={`pb-row-${i}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-graphite truncate">
                        {ex?.name || 'Unknown'}
                      </p>
                      <p className="text-[10px] uppercase tracking-[0.15em] text-graphite/50 font-semibold">
                        {ex?.muscle_group || '—'} · {format(new Date(pb.logged_at), 'MMM d, yyyy')}
                      </p>
                    </div>
                    <div className="text-right pl-2">
                      <p className="text-xl font-black font-heading tabular-nums text-userRed leading-none">
                        {cardio
                          ? <>{((pb.distance_m || 0) / 1000).toFixed(2)}<span className="text-xs font-normal">km</span></>
                          : <>{pb.weight}<span className="text-xs font-normal">kg</span></>}
                      </p>
                      <p className="text-[10px] uppercase tracking-[0.15em] text-graphite/50 font-semibold">
                        {cardio ? formatDur(pb.duration_seconds) : `× ${pb.reps} reps`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null,
    },
  };

  return (
    <div className="mobile-container min-h-screen">
      <div className="content-wrapper px-5 pt-10">
        <div className="mb-6 animate-slide-up flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar user={user} size={56} testId="profile-avatar" />
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.24em] text-graphite/50 font-semibold mb-0.5">
                Profile
              </p>
              <h1 className="text-4xl font-black font-heading uppercase tracking-tight text-graphite leading-none truncate">
                {user?.display_name || 'Guest'}
              </h1>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <button
              onClick={() => navigate('/settings')}
              className="w-10 h-10 rounded-ios-pill border border-hairline bg-bone hover:bg-graphite/5 flex items-center justify-center"
              data-testid="settings-button"
              aria-label="Open settings"
              title="Settings"
            >
              <Gear size={18} weight="bold" />
            </button>
            <button
              onClick={handleReset}
              className={`text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 rounded-ios-pill transition-colors ${
                resetArmed
                  ? 'text-bone bg-userRed shadow-ios'
                  : 'text-graphite/40 hover:text-graphite'
              }`}
              data-testid="logout-button"
              title={resetArmed ? 'Tap again to confirm' : 'Reset your guest account'}
            >
              {resetArmed ? 'Tap to Confirm' : 'Reset'}
            </button>
          </div>
        </div>

        {/* PINNED SUMMARY — never reorderable */}
        <div className="grid grid-cols-3 gap-2 mb-4" data-testid="pinned-quick-stats">
          <QuickStatCard label="Workouts" value={total?.total_workouts || 0} testId="stat-workouts" />
          <QuickStatCard label="Sets" value={total?.total_sets || 0} testId="stat-sets" />
          <QuickStatCard label="Reps" value={total?.total_reps || 0} testId="stat-reps" />
        </div>
        <div className="rounded-ios-lg p-5 mb-4 animate-slide-up shadow-ios" style={{ backgroundColor: '#191C21', color: '#EDEAE3' }} data-testid="total-volume-hero">
          <p className="text-[10px] uppercase tracking-[0.24em] font-bold text-bone/60 mb-1">
            Total Lifted
          </p>
          <p className="text-6xl font-black font-heading tabular-nums leading-none text-bone">
            {Math.round(total?.total_volume || 0).toLocaleString()}
            <span className="text-2xl font-normal ml-1">kg</span>
          </p>
          {topComparison && total.total_volume > 0 && (
            <p className="text-sm text-bone/80 mt-3">
              That's like lifting{' '}
              <span className="font-black text-bone tabular-nums">
                {topComparison.count}
              </span>{' '}
              {topComparison.label}.
            </p>
          )}
        </div>

        {/* Reorder toggle */}
        <div className="flex items-center justify-end mb-3">
          <button
            onClick={() => setReordering((v) => !v)}
            className={`text-[10px] uppercase tracking-[0.18em] font-black px-3 h-8 rounded-ios-pill flex items-center gap-1.5 ${
              reordering ? 'bg-userRed text-bone' : 'text-graphite/60 bg-graphite/5 hover:bg-graphite/10'
            }`}
            data-testid="reorder-profile-button"
          >
            <ArrowsDownUp size={12} weight="bold" />
            {reordering ? 'Done' : 'Reorder Sections'}
          </button>
        </div>

        {/* Reorderable sections */}
        {fullOrder.map((key, idx) => {
          const section = SECTIONS[key];
          if (!section) return null;
          const content = section.render();
          if (!content) return null;
          return (
            <div key={key} className="relative" data-testid={`section-${key}`}>
              {reordering && (
                <div
                  className="absolute -left-1 top-2 flex flex-col gap-1 z-10"
                  data-testid={`section-controls-${key}`}
                >
                  <button
                    onClick={() => moveSection(idx, -1)}
                    disabled={idx === 0}
                    className="w-7 h-7 rounded-full bg-userRed text-bone flex items-center justify-center shadow-ios disabled:opacity-30"
                    data-testid={`section-up-${key}`}
                    aria-label={`Move ${section.label} up`}
                  >
                    <ArrowUp size={12} weight="bold" />
                  </button>
                  <button
                    onClick={() => moveSection(idx, 1)}
                    disabled={idx === fullOrder.length - 1}
                    className="w-7 h-7 rounded-full bg-userRed text-bone flex items-center justify-center shadow-ios disabled:opacity-30"
                    data-testid={`section-down-${key}`}
                    aria-label={`Move ${section.label} down`}
                  >
                    <ArrowDown size={12} weight="bold" />
                  </button>
                </div>
              )}
              <div
                className={reordering ? 'ml-8 outline outline-2 outline-dashed outline-userRed/40 rounded-ios-lg' : ''}
              >
                {reordering && (
                  <p className="px-3 pt-2 text-[10px] uppercase tracking-[0.22em] font-black text-userRed">
                    {section.label}
                  </p>
                )}
                {content}
              </div>
            </div>
          );
        })}

        {reordering && (
          <button
            onClick={resetLayout}
            className="w-full h-11 text-[11px] uppercase tracking-[0.18em] font-bold text-graphite/50 hover:text-userRed"
            data-testid="reset-profile-layout"
          >
            Reset to default order
          </button>
        )}
      </div>
      {showLog && <LogMeasurementModal onSave={saveMeasurement} onClose={() => setShowLog(false)} />}
</div>
  );
}

function VolumePerspectiveCard({ comparisons, expanded, onToggle }) {
  const visible = expanded ? comparisons : comparisons.slice(0, 4);
  return (
    <div className="ios-card p-4 mb-4" data-testid="volume-perspective-card">
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/60">
          Volume Perspective
        </p>
        {comparisons.length > 4 && (
          <button
            onClick={onToggle}
            className="text-[10px] uppercase tracking-[0.18em] font-black text-userRed flex items-center gap-1"
            data-testid="volume-perspective-toggle"
          >
            {expanded ? 'Show Less' : `Show All (${comparisons.length})`}
            <CaretDown
              size={10}
              weight="bold"
              className={expanded ? 'rotate-180 transition-transform' : 'transition-transform'}
            />
          </button>
        )}
      </div>
      <div className="space-y-1.5">
        {visible.map((c, i) => (
          <div
            key={c.label}
            className="flex items-baseline justify-between text-sm"
            data-testid={`comparison-row-${i}`}
          >
            <span className="text-graphite/70 truncate pr-2">{c.label}</span>
            <span className="font-black font-heading tabular-nums text-graphite">
              ×{c.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MeasurementsCard({ measurements, onAdd, onDelete }) {
  const { user } = useAuth();
  const currentUnit = (user?.measurement_unit === 'mm') ? 'mm' : 'cm';
  const [unit, setUnit] = useState(currentUnit);

  const setServerUnit = async (u) => {
    setUnit(u);
    try {
      await axios.put(`${API_URL}/auth/me`, { measurement_unit: u }, { withCredentials: true });
    } catch (e) {
      console.warn('[breth] measurement unit sync failed, will retry on next load', e);
    }
  };

  const displayLen = (v, defaultLabel) => {
    if (v == null) return null;
    const val = unit === 'mm' ? Math.round(v * 10) : v;
    return `${val}${unit}`;
  };

  const latest = measurements[0];
  const weightPoints = useMemo(() => {
    return [...measurements]
      .reverse()
      .filter((m) => m.weight_kg != null)
      .map((m) => ({
        date: format(parseISO(m.logged_at), 'MMM d'),
        weight: m.weight_kg,
      }));
  }, [measurements]);

  return (
    <div className="ios-card p-4 mb-4" data-testid="measurements-card">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <Ruler size={16} weight="fill" className="text-graphite/60" />
          <h2 className="text-lg font-black font-heading uppercase text-graphite">Body</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-graphite/5 rounded-ios-pill p-0.5" data-testid="measurement-unit-toggle">
            {['cm','mm'].map((u) => (
              <button
                key={u}
                onClick={() => setServerUnit(u)}
                className={`h-7 px-3 rounded-ios-pill text-[10px] uppercase tracking-[0.18em] font-black ${
                  unit === u ? 'bg-graphite text-bone' : 'text-graphite/60'
                }`}
                data-testid={`measurement-unit-${u}`}
              >
                {u}
              </button>
            ))}
          </div>
          <button
            onClick={onAdd}
            className="h-9 px-3 rounded-ios-pill bg-userRed text-bone text-[10px] uppercase tracking-[0.18em] font-black flex items-center gap-1"
            data-testid="log-measurement-button"
          >
            <Plus size={12} weight="bold" /> Log
          </button>
        </div>
      </div>

      {!latest ? (
        <p className="text-sm text-graphite/60">
          Log body weight, height and tape measurements to see progress alongside your lifts.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {MEASUREMENT_FIELDS.filter((f) => latest[f.key] != null).slice(0, 6).map((f) => {
              // Weight and body-fat are unit-agnostic; length fields honour the unit toggle
              let display;
              if (f.unit === 'kg' || f.unit === '%') {
                display = `${latest[f.key]}${f.unit}`;
              } else {
                display = displayLen(latest[f.key]);
              }
              return (
                <div key={f.key} className="bg-boneWarm rounded-ios px-2 py-2">
                  <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-graphite/50 leading-none truncate">
                    {f.label}
                  </p>
                  <p className="text-xl font-black font-heading tabular-nums text-graphite leading-tight mt-0.5">
                    {display}
                  </p>
                </div>
              );
            })}
          </div>
          <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-graphite/40 mb-3">
            Last: {format(parseISO(latest.logged_at), 'MMM d, yyyy')}
          </p>

          {weightPoints.length >= 2 && (
            <div className="bg-boneWarm rounded-ios p-2 mb-3">
              <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1">
                Weight Trend
              </p>
              <ResponsiveContainer width="100%" height={110}>
                <LineChart data={weightPoints}>
                  <CartesianGrid stroke="#DCD7CB" vertical={false} />
                  <XAxis dataKey="date" stroke="#191C21" style={{ fontSize: '9px' }} />
                  <YAxis stroke="#191C21" style={{ fontSize: '10px' }} domain={['dataMin - 1', 'dataMax + 1']} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#191C21', color: '#EDEAE3', border: 'none', borderRadius: '10px' }}
                    itemStyle={{ color: '#EDEAE3' }}
                  />
                  <Line type="monotone" dataKey="weight" stroke="#C0392F" strokeWidth={2.5} dot={{ fill: '#C0392F', r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {measurements.length > 1 && (
            <details className="text-sm">
              <summary className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 cursor-pointer py-1">
                History ({measurements.length})
              </summary>
              <div className="mt-2 space-y-1 max-h-52 overflow-y-auto">
                {measurements.map((m) => (
                  <div key={m.id} className="flex items-center justify-between px-2 py-1.5 rounded-ios bg-graphite/[0.03]" data-testid={`measurement-row-${m.id}`}>
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-graphite/60 w-20">
                      {format(parseISO(m.logged_at), 'MMM d')}
                    </div>
                    <div className="flex-1 text-xs text-graphite/80 truncate">
                      {MEASUREMENT_FIELDS.filter((f) => m[f.key] != null).map((f) => {
                        const val = (f.unit === 'kg' || f.unit === '%')
                          ? `${m[f.key]}${f.unit}`
                          : displayLen(m[f.key]);
                        return `${f.label} ${val}`;
                      }).join(' · ')}
                    </div>
                    <button
                      onClick={() => onDelete(m.id)}
                      className="w-7 h-7 flex items-center justify-center text-graphite/30 hover:text-userRed"
                      aria-label="Delete entry"
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}

function LogMeasurementModal({ onSave, onClose }) {
  const { user } = useAuth();
  const unit = user?.measurement_unit === 'mm' ? 'mm' : 'cm';
  const [vals, setVals] = useState({});
  const update = (k, v) => setVals((prev) => ({ ...prev, [k]: v }));
  const submit = () => {
    const payload = {};
    for (const f of MEASUREMENT_FIELDS) {
      const raw = vals[f.key];
      if (raw !== '' && raw != null) {
        let num = parseFloat(raw);
        if (isNaN(num)) continue;
        // Length fields (unit === 'cm') should be converted if user entered in mm
        if (f.unit === 'cm' && unit === 'mm') num = num / 10;
        payload[f.key] = num;
      }
    }
    if (Object.keys(payload).length === 0) {
      alert('Enter at least one measurement.');
      return;
    }
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-[1100] bg-graphite/40 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 pb-3">
      <div className="w-full max-w-md bg-bone rounded-ios-xl shadow-ios overflow-hidden max-h-[85vh] flex flex-col animate-slide-up">
        <div className="px-5 py-4 border-b border-hairline flex items-center justify-between">
          <h2 className="text-2xl font-black font-heading uppercase text-graphite">Log Body</h2>
          <button onClick={onClose} className="w-10 h-10 rounded-ios-pill border border-hairline flex items-center justify-center" data-testid="close-measurement-modal">
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto space-y-3">
          {MEASUREMENT_FIELDS.map((f) => {
            // Length fields honour the user's unit preference; weight/pct do not
            const displayUnit = (f.unit === 'cm') ? unit : f.unit;
            return (
              <div key={f.key} className="flex items-center gap-3">
                <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-graphite/60 w-24">
                  {f.label}
                </label>
                <div className="flex-1 relative">
                  <input
                    type="number"
                    step={f.key === 'weight_kg' || f.key === 'body_fat_pct' ? '0.1' : (unit === 'mm' ? '1' : '0.5')}
                    inputMode="decimal"
                    value={vals[f.key] ?? ''}
                    onChange={(e) => update(f.key, e.target.value)}
                    className="w-full h-11 px-4 pr-10 rounded-ios-pill border border-hairline bg-boneWarm focus:outline-none focus:ring-2 focus:ring-graphite text-sm"
                    data-testid={`measurement-input-${f.key}`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-graphite/40 uppercase">{displayUnit}</span>
                </div>
              </div>
            );
          })}
          <button
            onClick={submit}
            className="w-full h-14 bg-userRed text-bone font-black text-sm uppercase tracking-[0.15em] rounded-ios-pill"
            data-testid="save-measurement-button"
          >
            Save Entry
          </button>
        </div>
      </div>
    </div>
  );
}

function QuickStatCard({ label, value, testId }) {
  return (
    <div className="ios-card px-3 py-3" data-testid={testId}>
      <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-graphite/50 leading-none">{label}</p>
      <p className="text-3xl font-black font-heading tabular-nums text-graphite leading-tight mt-1">{value}</p>
    </div>
  );
}

function StatCard({ label, value, unit }) {
  return (
    <div className="bg-boneWarm rounded-ios p-3">
      <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-graphite/50 leading-none">{label}</p>
      <p className="text-3xl font-black font-heading tabular-nums text-graphite leading-tight mt-1">
        {value}
        {unit && <span className="text-sm font-normal ml-0.5">{unit}</span>}
      </p>
    </div>
  );
}

function formatDur(secs) {
  if (!secs) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m === 0) return `${s}s`;
  if (s === 0) return `${m}m`;
  return `${m}m ${s}s`;
}
